import { useState } from 'react';
import  { type CallsForLead, type CallsForLeadQueryParams, } from '@intone/shared';

import { formatTimestamp, formatDuration } from '@/shared/lib/date';
import { formatDisconnectionReason } from '@/shared/lib/strings';
import { normalizePhoneNumber } from '@/shared/lib/phones';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { useCallsForLead } from '@/entities/call';
import { DispositionBadge } from '@/features/dispositionBadge';

type LeadCallsTableProps = {
  phone: string
  onSelect: (call_id: string) => void
}

export function LeadCallsTable({ phone, onSelect }: LeadCallsTableProps) {
  // Normalize phone to digits only
  const normalizedPhone = normalizePhoneNumber(phone || '');

  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState<number>(0);
  const pageSize = 50;

  // Sorting
  const [sortField, setSortField] = useState<string>('start_timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Build query params
  const queryParams: CallsForLeadQueryParams = {
    page_id: currentPage,
    page_size: pageSize,
    order_by: sortField as CallsForLeadQueryParams['order_by'],
    order_type: sortDirection,
  }

  const { data, isLoading, error } = useCallsForLead(
    normalizedPhone,
    queryParams,
  );

  const rows = data?.calls || []
  const totalCount = data?.totalCount || 0

  const columns: Column<CallsForLead>[] = [
    {
      header: 'Start time',
      accessorKey: 'number',
      sortField: 'start_timestamp',
      sortable: true,
      cell: (value: number | null) => formatTimestamp(value),
      width: { min: 180, width: 180 },
    },
    {
      header: 'Disposition',
      accessorKey: 'disposition_status',
      sortField: 'disposition_status',
      sortable: true,
      cell: (value: string | null) => (
        <DispositionBadge
          disposition={value || ''}
          isEditable={false}
        />
      ),
      width: { min: 120, width: 120 },
    },
    {
      header: 'Duration',
      accessorKey: 'total_duration_seconds',
      sortField: 'total_duration_seconds',
      sortable: true,
      cell: (value: number | null) => formatDuration(value),
      width: { min: 88, width: 88 },
    },
    {
      header: 'Disconnect reason',
      accessorKey: 'disconnection_reason',
      sortField: 'disconnection_reason',
      sortable: true,
      cell: (value: string | null) => formatDisconnectionReason(value),
      width: { min: 140 },
    },
  ]

  return (
    <DataTable<CallsForLead>
      columns={columns}
      data={rows}
      loading={isLoading}
      error={error?.message}
      pagination={{currentPage, pageSize, totalCount}}
      onPageChange={setCurrentPage}
      sorting={{ sortField, sortDirection }}
      onSortChange={(sortField, sortDirection) => {
        setSortField(sortField)
        setSortDirection(sortDirection)
      }}
      onRowClick={(row) => {
        onSelect(row.id)
        setSelectedId(row.id)
      }}
      selectedId={selectedId}
      emptyMessage={'No calls found'}
    />
  )
}